import axios from 'axios';

// Use same-origin in the browser to avoid CORS entirely in production
// (React app on azaibilling.com will call azaibilling.com/api/...).
// Falls back to REACT_APP_BACKEND_URL for environments where the frontend
// is served from a different origin than the backend (local dev, etc.).
const API_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Phase 4 tenant isolation: send the selected agency on every request. The
// backend validates it against real memberships/elevations — it is a
// selection hint, never an authorization grant.
api.interceptors.request.use((config) => {
  const agencyId = localStorage.getItem('currentAgencyId');
  if (agencyId) {
    config.headers['X-Agency-Id'] = agencyId;
  }
  return config;
});

// Normalize FastAPI/Pydantic v2 validation errors. Pydantic v2 returns
// `detail` as `[{type, loc, msg, input, url}, ...]` — passing that array
// of objects to `toast.error()` or rendering it directly in JSX throws
// `Objects are not valid as a React child`. Coerce to a readable string
// once here so every caller can keep doing `err.response?.data?.detail
// || 'fallback'` without crashing the app on 422 responses.
const normalizeDetailToString = (detail) => {
  if (!detail) return detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const field = Array.isArray(d.loc)
            // strip the leading 'body' / 'query' / 'path' segment from Pydantic loc
            ? d.loc.filter((seg) => seg !== 'body' && seg !== 'query' && seg !== 'path').join('.')
            : '';
          const msg = d.msg || d.message || JSON.stringify(d);
          return field ? `${field}: ${msg}` : msg;
        }
        return String(d);
      })
      .join('; ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

// Response interceptor: redirect to login on 401 + normalize Pydantic errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/')) {
      window.location.href = '/login';
    }
    if (error.response?.data?.detail !== undefined) {
      // Preserve structured semantic errors (e.g. mfa_step_up_required)
      // before flattening detail to a display string.
      error.response.data.detail_object = error.response.data.detail;
      error.response.data.detail = normalizeDetailToString(error.response.data.detail);
    }
    return Promise.reject(error);
  }
);

// Patients API
export const patientsApi = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  // Manual PNM-verified override — confirms Medicaid ID + name + DOB +
  // gender against the Ohio PNM portal. Short-circuits preflight
  // B1/B3/B4 false positives from the mock eligibility layer.
  verifyPnm: (id, body) => api.post(`/patients/${id}/verify-pnm`, body),
};

// Claims API
export const claimsApi = {
  getAll: (params) => api.get('/claims', { params }),
  getById: (id) => api.get(`/claims/${id}`),
  create: (data) => api.post('/claims', data),
  update: (id, data) => api.put(`/claims/${id}`, data),
  delete: (id) => api.delete(`/claims/${id}`),
  submit: (id) => api.post(`/claims/${id}/submit`),
  copy: (id, newDate) => api.post(`/claims/${id}/copy`, null, { params: { new_service_date: newDate } }),
  triage: (id, action, notes) => api.post(`/claims/${id}/triage`, { action, notes }),
  correct: (id, updates) => api.post(`/claims/${id}/correct`, { updates: updates || {} }),
  void: (id) => api.post(`/claims/${id}/void`, {}),
  // P6 (May 2026 Rhinobill parity): mark a Rejected/Denied claim as
  // "Fixed" (in-rework tracking). On the next 277CA accept, this elevates
  // to "Fix Successful" via the EDI response poller.
  markFixed: (id) => api.post(`/claims/${id}/mark-fixed`),
  // P34 (Feb 27, 2026): Rhinobill claims-list filter rail backing endpoints.
  filterOptions: () => api.get('/claims/filter-options'),
  exportCsv: (params) => api.get('/claims/export.csv', { params, responseType: 'text' }),
  listSavedViews: () => api.get('/claims/saved-views'),
  createSavedView: (body) => api.post('/claims/saved-views', body),
  deleteSavedView: (id) => api.delete(`/claims/saved-views/${id}`),
};

// Templates API
export const templatesApi = {
  getAll: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  delete: (id) => api.delete(`/templates/${id}`),
  use: (id) => api.post(`/templates/${id}/use`),
};

// Codes API
export const codesApi = {
  getAll: (params) => api.get('/codes', { params }),
  getById: (code) => api.get(`/codes/${code}`),
  getModifiers: () => api.get('/codes/modifiers/all'),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Billing Hub API
export const billingApi = {
  getHubStats: () => api.get('/billing/hub-stats'),
};

// ODM SFTP Credentials API (P37 — Feb 28, 2026)
// AZAI super-admin endpoints for managing CERT + PROD ODM credentials.
export const odmCredentialsApi = {
  list: () => api.get('/admin/odm-credentials'),
  update: (env, body) => api.put(`/admin/odm-credentials/${env}`, body),
  test: (env) => api.post(`/admin/odm-credentials/${env}/test`),
  activate: (env) => api.post(`/admin/odm-credentials/${env}/activate`, { confirm: 'I_UNDERSTAND' }),
  // Lightweight active-env probe — used by the global OdmEnvBadge.
  getActive: () => api.get('/admin/odm-credentials/active'),
};

// ODM Host-Key Pinning API (CERT prerequisite — Jun 2026)
// Capture → independent verification → pin → preflight. AZAI admins only.
export const odmHostKeysApi = {
  status: (env) => api.get(`/admin/odm-host-keys/${env}`),
  capture: (env) => api.post(`/admin/odm-host-keys/${env}/capture`),
  verify: (env, body) => api.post(`/admin/odm-host-keys/${env}/verify`, body),
  reject: (env, body) => api.post(`/admin/odm-host-keys/${env}/reject`, body),
  preflight: (env, attemptAuth = false) =>
    api.post(`/admin/odm-host-keys/${env}/preflight`, { attempt_auth: attemptAuth }),
};

export default api;
